import React, { useState } from 'react';
import {
  FaSearch, FaFilter, FaAngleLeft, FaAngleDoubleLeft, FaAngleRight, FaAngleDoubleRight, FaThList, FaCaretDown,
} from 'react-icons/fa';
import WhenFields, { useWhenState, FindEventsChips } from './WhenFields';
import WhenButton from './WhenButton';
import ViewList from '../live/ViewList';
import Sheet from '../common/Sheet';
import history from '../history';
import {
  MCPB_SPANS, SPAN_SECONDS, windowSeconds, chipTime, shortDate,
} from '../util/mcpbTime';

/**
 * MCPlaybackRibbon — the mobile-portrait control ribbon (the thumb zone below the transport). It
 * consolidates every playback time-nav control into one thumb-reachable strip:
 *   • window/day paging  ◀◀ ◀ [when] ▶ ▶▶   (pageMcpbWindow — P-N7)
 *   • date/time + search chip → a WhenFields bottom-sheet  (this is what lets the mobile path drop the
 *     DtModal bridge — reuse the SAME field block Live/DtModal use)
 *   • span/zoom presets  1h · 4h · 8h · 24h   (setMcpbSpan — P-N5/N6, ports Java MCTimelineWindow)
 *   • Filters funnel + active-count badge → a FindEventsChips sheet  (client-side, no refetch — P-P4)
 *   • Views picker → Live's ViewList sheet  (playback reuses the shared Live views — P-Q3)
 *
 * Mobile-portrait ONLY (CSS-gated to the base); tablet/desktop keep their sidebars until Phase D.
 * Presentational shell — the parent owns the committed search and dispatches the thunks.
 */
const MCPlaybackRibbon = ({
  selectedTimestamp, searchWindow, nvrTimestampShort, filters = {}, playheadTs, currentViewName, viewDirty,
  onSearch, onSetSpan, onPage, onSetFilters, onRequestPresence,
  parts = null,
}) => {
  const [sheet, setSheet] = useState(null); // 'when' | 'filters' | 'views' | null — one sheet at a time
  const closeSheet = () => setSheet(null);
  const has = (p) => !parts || parts.includes(p);

  const activeSpanIdx = SPAN_SECONDS.indexOf(windowSeconds(searchWindow));
  // The window step jumps by exactly the selected span, so label the paging buttons with that amount
  // instead of cryptic arrows (David #86bb7phhh): -4h/+4h for the window, -1d/+1d for the day.
  const spanLabel = (MCPB_SPANS[activeSpanIdx] && MCPB_SPANS[activeSpanIdx].label) || (Math.round(windowSeconds(searchWindow) / 3600) + 'h');
  // At the 24h span, one WINDOW = one DAY, so the window step (‹24h/24h›) would just duplicate the day
  // double-arrows (« / ») — at 24h hide the WINDOW pair and keep the day arrows (David #86bb7phhh).
  const is24hSpan = windowSeconds(searchWindow) === 24 * 3600;
  const filterCount = Object.keys(filters).filter((k) => filters[k]).length;
  const whenDate = playheadTs ? shortDate(playheadTs) : '';
  const whenTime = playheadTs ? chipTime(playheadTs).slice(0, 5) : '--:--';
  // The year rides in its OWN sub-span (WhenButton) so the wide tablet hero can show it ("Aug 7, 2026")
  // while the compact mobile bottom row hides it via CSS ("Aug 7"). Only meaningful in parts/portrait mode.
  const whenYear = playheadTs ? String(playheadTs).slice(0, 4) : '';

  const viewsBtn = (
    <button className="mcpb-ribbon__tool mcpb-ribbon__tool--views" type="button" onClick={() => setSheet('views')} aria-label="Views">
      <FaThList aria-hidden="true" />
      <span className="mcpb-ribbon__tool-label mcpb-ribbon__tool-label--view" title={currentViewName || 'Views'}>{currentViewName || 'Views'}</span>
      {/* Dropdown caret — the SAME FaCaretDown as the WhenButton (cohesion); CSS-revealed only in the
          mobile bottom-row scope (.mcpb-whenrow__views), so the rail/tablet Views tool is unchanged. */}
      <FaCaretDown className="mcpb-ribbon__tool-caret" aria-hidden="true" />
      {/* Goldenrod "unsaved rearrange" dot — same signal as Live's action-bar Views dot. */}
      {viewDirty && <span className="mcpb-ribbon__tool-dot" title="Unsaved changes" />}
    </button>
  );
  const filtersBtn = (
    <button className={`mcpb-ribbon__tool${filterCount ? ' mcpb-ribbon__tool--on' : ''}`} type="button" onClick={() => setSheet('filters')} aria-label="Filters">
      <FaFilter aria-hidden="true" />
      <span className="mcpb-ribbon__tool-label">Filters</span>
      {filterCount > 0 && <span className="mcpb-ribbon__badge">{filterCount}</span>}
    </button>
  );
  const spansGroup = (
    <div className="mcpb-ribbon__spans" role="group" aria-label="Timeline span">
      {MCPB_SPANS.map((s, i) => (
        <button
          key={s.key}
          type="button"
          className={`mcpb-ribbon__span${i === activeSpanIdx ? ' mcpb-ribbon__span--active' : ''}`}
          aria-pressed={i === activeSpanIdx}
          title={`Show ${s.label} on the timeline`}
          onClick={() => onSetSpan(s.seconds)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
  // Nav row: day/window paging flanking the WhenButton. The day pair is hidden at 24h (it would duplicate
  // the window pair). In parts/portrait mode the WhenButton goes `hero` (full-width).
  const navRow = (
    <div className="mcpb-ribbon__nav">
      <button className="mcpb-ribbon__page mcpb-ribbon__page--day" type="button" aria-label="Back one day" title="Back one day" onClick={() => onPage(-1, 'day')}><FaAngleDoubleLeft /></button>
      {/* Window step. At 24h it coincides with the day step, so RESERVE it (visibility:hidden) rather than
          remove it — the row keeps its width and doesn't jump when switching to/from 24h (David #86bb7phhh). */}
      <button className={'mcpb-ribbon__page mcpb-ribbon__page--label' + (is24hSpan ? ' mcpb-ribbon__page--reserved' : '')} type="button" aria-hidden={is24hSpan || undefined} tabIndex={is24hSpan ? -1 : undefined} aria-label={'Back ' + spanLabel} title={'Back ' + spanLabel} onClick={() => { if (!is24hSpan) onPage(-1, 'window'); }}><FaAngleLeft />{spanLabel}</button>
      <WhenButton date={whenDate} year={parts ? whenYear : undefined} time={whenTime} hero={!!parts} onClick={() => setSheet('when')} />
      <button className={'mcpb-ribbon__page mcpb-ribbon__page--label' + (is24hSpan ? ' mcpb-ribbon__page--reserved' : '')} type="button" aria-hidden={is24hSpan || undefined} tabIndex={is24hSpan ? -1 : undefined} aria-label={'Forward ' + spanLabel} title={'Forward ' + spanLabel} onClick={() => { if (!is24hSpan) onPage(1, 'window'); }}>{spanLabel}<FaAngleRight /></button>
      <button className="mcpb-ribbon__page mcpb-ribbon__page--day" type="button" aria-label="Forward one day" title="Forward one day" onClick={() => onPage(1, 'day')}><FaAngleDoubleRight /></button>
    </div>
  );

  const sheets = (
    <>
      {/* Bottom sheets (thumb-zone). Each renders through the shared <Sheet> (grip + corner ✕ + body). */}
      {sheet === 'when' && (
        <WhenSheet
          initial={{ selectedTimestamp, window: searchWindow, filters }}
          nvrTimestampShort={nvrTimestampShort}
          onRequestPresence={onRequestPresence}
          onGo={(v) => { onSearch(v); closeSheet(); }}
          onClose={closeSheet}
        />
      )}

      {sheet === 'filters' && (
        <Sheet className="mcpb-sheet" scrimClassName="mcpb-sheet-scrim" title="Filter events" ariaLabel="Filters" onClose={closeSheet}>
          <p className="mcpb-sheet__hint">Show only clips tagged with the selected types (filters the loaded window &mdash; no re-search).</p>
          <FindEventsChips filters={filters} onToggle={(k) => onSetFilters({ ...filters, [k]: !filters[k] })} />
          <button className="mcpb-sheet__done" type="button" onClick={closeSheet}>Done</button>
        </Sheet>
      )}

      {sheet === 'views' && (
        <Sheet className="mcpb-sheet mcpb-sheet--views" scrimClassName="mcpb-sheet-scrim" title="Views" ariaLabel="Views" onClose={closeSheet}>
          {/* The SHARED ViewList (identical to Live). Selecting a row dispatches selectView → the shared
              currentView → the reload-on-view-change effect in MCPlaybackView re-materializes the grid
              (P-Q3). Arrange current / New view (tablet+ only; mobile hides them via CSS) + the per-view ✎
              ROUTE to Live's Arrange editor for the SAME shared view and return here on Done — Option A,
              #86bb7phhh. skipDirtyGuard = never prompt to save on switch (David: don't care if they save). */}
          <ViewList
            onArrange={(v) => { closeSheet(); history.push('/live', { arrangeRequest: { view: v }, returnTo: '/mcplayback' }); }}
            onArrangeCurrent={() => { closeSheet(); history.push('/live', { arrangeRequest: 'current', returnTo: '/mcplayback' }); }}
            onNewView={() => { closeSheet(); history.push('/live', { arrangeRequest: 'new', returnTo: '/mcplayback' }); }}
            onClose={closeSheet}
            skipDirtyGuard
          />
        </Sheet>
      )}
    </>
  );

  // Parts mode (Option A portrait, #86bb7phhh S20): return just the requested BARE pieces — the parent
  // (MCPlaybackView) places them (Filters + zoom on the timeline card, the when-nav as the "Go to date &
  // time" hero row, Views on the banks row). Each mount owns its own sheet state. Default (no parts) = the
  // full ribbon (the landscape rail).
  if (parts) {
    return (
      <>
        {has('views') && viewsBtn}
        {has('filters') && filtersBtn}
        {has('zoom') && spansGroup}
        {has('when') && navRow}
        {sheets}
      </>
    );
  }
  return (
    <div className="mcpb-ribbon">
      <div className="mcpb-ribbon__tools">{viewsBtn}{filtersBtn}{spansGroup}</div>
      {navRow}
      {sheets}
    </div>
  );
};

// The WHEN sheet — the mobile "Go to time" picker (WhenFields in ANCHOR mode: single-tap month-grid
// calendar with green availability dots + start-time only). Commits DATE + START TIME; the window WIDTH
// is the ribbon span (one source), so onGo carries just { anchorTimestamp }. Owns only the working
// selection (useWhenState, seeded once on open). onRequestPresence feeds the calendar's dots.
const WhenSheet = ({ initial, nvrTimestampShort, onRequestPresence, onGo, onClose }) => {
  const w = useWhenState({ initial, nvrTimestampShort, anchorOnly: true });
  return (
    // No visible heading (David 2026-08-04) — the calendar + "Start time" + Search are self-evident, so the
    // calendar is the hero. The grip + the corner ✕ (from the shared Sheet) are the only chrome; the dialog
    // keeps an aria-label so screen readers still get a name.
    <Sheet className="mcpb-sheet mcpb-sheet--when" scrimClassName="mcpb-sheet-scrim" ariaLabel="Choose date and time" onClose={onClose}>
      <div className="mcpb-sheet__body">
        <WhenFields w={w} nvrTimestampShort={nvrTimestampShort} variant="anchor" onRequestPresence={onRequestPresence} />
        <button className="mcpb-sheet__done mcpb-sheet__done--search" type="button" onClick={() => { if (w.valid) onGo(w.value); }} disabled={!w.valid}>
          <FaSearch aria-hidden="true" /> Search
        </button>
      </div>
    </Sheet>
  );
};

export default MCPlaybackRibbon;
